import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "menu"]
  static values = { open: Boolean }

  connect() {
    console.log("🔧 Sidebar controller connected successfully")
    
    // 상태 초기화 - Turbo Drive 페이지 전환 시에도 안전하게 초기화
    this.initializeState()
    
    // 이벤트 리스너 설정
    this.setupEventListeners()
    
    // 타겟 존재 확인
    this.validateTargets()
    
    console.log("📱 Menu target found:", this.hasMenuTarget)
    console.log("🌫️ Overlay target found:", this.hasOverlayTarget)
  }

  disconnect() {
    console.log("🔧 Sidebar controller disconnected")
    
    // 상태 정리 - body overflow 복원
    this.cleanupState()
    
    // 이벤트 리스너 제거
    this.removeEventListeners()
  }

  // 상태 초기화
  initializeState() {
    // 기본값 설정
    this.openValue = this.openValue || false
    
    // body 스크롤 상태 복원 (Turbo Drive 페이지 전환 시 중요)
    if (!this.openValue) {
      document.body.style.overflow = ''
    }
    
    // DOM 상태와 내부 상태 동기화
    this.syncStateWithDOM()
  }

  // DOM과 내부 상태 동기화
  syncStateWithDOM() {
    if (this.hasOverlayTarget && this.hasMenuTarget) {
      const isOverlayVisible = !this.overlayTarget.classList.contains('hidden')
      const isMenuVisible = this.menuTarget.classList.contains('translate-x-0')
      
      if (isOverlayVisible && isMenuVisible) {
        this.openValue = true
        document.body.style.overflow = 'hidden'
      } else {
        this.openValue = false
        document.body.style.overflow = ''
      }
    }
  }

  // 상태 정리
  cleanupState() {
    // body 스크롤 복원
    document.body.style.overflow = ''
    
    // 타이머 정리
    if (this.animationTimer) {
      clearTimeout(this.animationTimer)
      this.animationTimer = null
    }
  }

  // 이벤트 리스너 설정
  setupEventListeners() {
    // ESC 키로 사이드바 닫기
    if (this._handleEscapeKey) {
      document.removeEventListener('keydown', this._handleEscapeKey)
    }
    this._handleEscapeKey = this.handleEscapeKey.bind(this)
    document.addEventListener('keydown', this._handleEscapeKey)
    
    // 윈도우 리사이즈 시 큰 화면에서 사이드바 자동 닫기
    if (this._handleWindowResize) {
      window.removeEventListener('resize', this._handleWindowResize)
    }
    this._handleWindowResize = this.handleWindowResize.bind(this)
    window.addEventListener('resize', this._handleWindowResize)
    
    // Turbo 캐시 전 정리
    if (this._handleBeforeCache) {
      document.removeEventListener('turbo:before-cache', this._handleBeforeCache)
    }
    this._handleBeforeCache = this.handleBeforeCache.bind(this)
    document.addEventListener('turbo:before-cache', this._handleBeforeCache)
  }

  // 이벤트 리스너 제거
  removeEventListeners() {
    if (this._handleEscapeKey) {
      document.removeEventListener('keydown', this._handleEscapeKey)
      this._handleEscapeKey = null
    }
    if (this._handleWindowResize) {
      window.removeEventListener('resize', this._handleWindowResize)
      this._handleWindowResize = null
    }
    if (this._handleBeforeCache) {
      document.removeEventListener('turbo:before-cache', this._handleBeforeCache)
      this._handleBeforeCache = null
    }
  }

  // ESC 키 처리
  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.openValue) {
      this.close()
    }
  }

  // 윈도우 리사이즈 처리
  handleWindowResize() {
    // 큰 화면에서는 사이드바 자동 닫기
    if (window.innerWidth >= 1024 && this.openValue) {
      this.close()
    }
  }

  // Turbo 캐시 전 상태 정리
  handleBeforeCache() {
    if (this.openValue) {
      console.log("Sidebar: closing before cache to prevent state issues")
      this.closeImmediately()
    }
  }

  // 애니메이션 없이 즉시 닫기 (Turbo 전환 시 사용)
  closeImmediately() {
    console.log("Sidebar: closing immediately")
    
    this.openValue = false
    
    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove("translate-x-0")
      this.menuTarget.classList.add("-translate-x-full")
    }
    
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
      this.overlayTarget.style.display = 'none'
    }
    
    // body 스크롤 즉시 복원
    document.body.style.overflow = ''
    
    // 타이머 정리
    if (this.animationTimer) {
      clearTimeout(this.animationTimer)
      this.animationTimer = null
    }
    
    console.log("Sidebar: closed immediately for cache")
  }

  // 타겟 유효성 검사
  validateTargets() {
    if (!this.hasOverlayTarget) {
      console.error("❌ Sidebar: overlay target not found")
    }
    if (!this.hasMenuTarget) {
      console.error("❌ Sidebar: menu target not found")
    }
  }

  toggle() {
    console.log("🎯 Toggle clicked, current state:", this.openValue)
    
    if (!this.hasOverlayTarget || !this.hasMenuTarget) {
      console.error("❌ Required targets not found for toggle")
      return
    }
    
    try {
      if (this.openValue) {
        this.close()
      } else {
        this.open()
      }
    } catch (error) {
      console.error("❌ Error in toggle:", error)
    }
  }

  open() {
    if (this.openValue) {
      console.log("🚪 Sidebar already open")
      return
    }
    
    console.log("🚪 Opening sidebar...")
    
    if (!this.hasOverlayTarget || !this.hasMenuTarget) {
      console.error("❌ Required targets not found for open")
      return
    }
    
    try {
      // 상태 업데이트
      this.openValue = true
      
      // body 스크롤 막기
      document.body.style.overflow = 'hidden'
      console.log("🔒 Body scroll disabled")
      
      // 오버레이 표시
      this.overlayTarget.classList.remove("hidden")
      this.overlayTarget.style.display = 'block'
      console.log("✅ Overlay shown")
      
      // 메뉴 애니메이션 (약간의 지연 후)
      this.animationTimer = setTimeout(() => {
        if (this.hasMenuTarget) {
          this.menuTarget.classList.remove("-translate-x-full")
          this.menuTarget.classList.add("translate-x-0")
          console.log("✅ Menu animation applied")
        }
      }, 10)
      
      console.log("✅ Sidebar opened successfully")
    } catch (error) {
      console.error("❌ Error opening sidebar:", error)
      // 에러 발생 시 상태 복원
      this.openValue = false
      document.body.style.overflow = ''
    }
  }

  close() {
    if (!this.openValue) {
      console.log("🚪 Sidebar already closed")
      return
    }
    
    console.log("🚪 Closing sidebar...")
    
    if (!this.hasOverlayTarget || !this.hasMenuTarget) {
      console.error("❌ Required targets not found for close")
      return
    }
    
    try {
      // 상태 업데이트
      this.openValue = false
      
      // 메뉴 숨김 애니메이션
      this.menuTarget.classList.remove("translate-x-0")
      this.menuTarget.classList.add("-translate-x-full")
      console.log("✅ Menu hiding animation applied")
      
      // 애니메이션 완료 후 오버레이 숨김 및 body 스크롤 복원
      this.animationTimer = setTimeout(() => {
        if (this.hasOverlayTarget) {
          this.overlayTarget.classList.add("hidden")
          this.overlayTarget.style.display = 'none'
          console.log("✅ Overlay hidden")
        }
        
        // body 스크롤 복원
        document.body.style.overflow = ''
        console.log("🔓 Body scroll restored")
      }, 300)
      
      console.log("✅ Sidebar closing initiated")
    } catch (error) {
      console.error("❌ Error closing sidebar:", error)
      // 에러 발생 시에도 body 스크롤 복원
      document.body.style.overflow = ''
    }
  }

  // 오버레이 클릭 시 사이드바 닫기
  closeOnOverlayClick() {
    this.close()
  }
}
